<script>
    import { onMount, onDestroy } from "svelte";
    import pb from '../../lib/pocketbase'; 
    import { toasts } from "svelte-toasts";

    let chatPanelOpen = false;
    let chatPanelRef;

    let messages = [];
    let newMessage = "";
    let userId;
    let selectedRecipients = []; // Para almacenar los destinatarios seleccionados
    let allUsers = []; // Lista de todos los usuarios
    let replyToMessageId = null; // Almacena el ID del mensaje al que se está respondiendo
    let replyToMessageContent = ""; // Almacena el contenido del mensaje al que se responde
    let errorMessage = ""; // Para almacenar mensajes de error
    let unreadCount = 0;

    const toggleChatPanel = (event) => {
        event.preventDefault();
        chatPanelOpen = !chatPanelOpen;

        if (chatPanelOpen) {
            chatPanelRef.classList.remove('hidden');
            chatPanelRef.classList.remove('translate-x-full');
            chatPanelRef.classList.add('translate-x-0');
        } else {
            chatPanelRef.classList.add('hidden');
        }
    };

    const closeChatPanel = (event) => {
        event.preventDefault();
        chatPanelOpen = false;
        chatPanelRef.classList.add('hidden');
        selectedRecipients = []; // Limpiar los destinatarios seleccionados
        markMessagesAsRead(); // Llama a la función para marcar mensajes como leídos
    };

    const getUserFromLocalStorage = () => {
        const storedUser = localStorage.getItem('pb_user');
        return storedUser ? JSON.parse(storedUser) : null;
    };

    const fetchMessages = async () => {
        try {
            const userId = getUserFromLocalStorage().id; // Obtener el ID del usuario actual
            const result = await pb.collection("mensaje").getFullList({
                sort: '-created',
                $autoCancel: false,
                expand: ['user,reply'],
                filter: `recipient = "${userId}" || user = "${userId}"`, // Filtrar mensajes donde el usuario actual es un destinatario o el remitente
            });

            messages = result.map((message) => {
                return {
                    id: message.id,
                    content: message.content,
                    user: message.user,
                    userName: message.expand.user.name || 'Usuario desconocido',
                    created: message.created,
                    recipient: message.recipient,
                    replyContent: message.expand.reply ? message.expand.reply.content : null,
                    replyCreated: message.expand.reply ? message.expand.reply.created : null,
                    sta: message.sta,
                };
            });

            unreadCount = countUnreadMessages(); // Actualizar el conteo de mensajes no leídos
        } catch (error) {
            console.error("Error al obtener los mensajes:", error);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const result = await pb.collection("users").getFullList({
                $autoCancel: false,
            });
            allUsers = result;
        } catch (error) {
            console.error("Error al obtener los usuarios:", error);
        }
    };

    const sendMessage = async () => {
        errorMessage = ""; 
        
        if (!newMessage.trim()) {
            errorMessage = "El contenido del mensaje no puede estar vacío."; // Mensaje de error para el contenido vacío
            return; // Salir de la función
        }

        if (selectedRecipients.length === 0) {
            errorMessage = "Debes seleccionar al menos un destinatario."; // Mensaje de error para destinatarios vacíos
            return; // Salir de la función
        }

        try {
            console.log("Enviando mensajes...");

            for (const recipientId of selectedRecipients) {
                console.log("Enviando mensaje a:", recipientId);

                const userId = getUserFromLocalStorage().id;
                if (!userId) {
                    throw new Error("El ID del usuario no es válido.");
                }

                console.log("Enviando mensaje a:", replyToMessageId);
                await pb.collection("mensaje").create({
                    content: newMessage,
                    user: userId,
                    recipient: recipientId,
                    sta: true,
                    reply: replyToMessageId, // Añadir la respuesta al mensaje si existe
                });

                console.log("Mensaje enviado con éxito a:", recipientId);
            }

            await fetchMessages(); // Refrescar la lista de mensajes
            newMessage = ""; // Limpiar el campo de entrada
            selectedRecipients = []; // Limpiar los destinatarios seleccionados
            replyToMessageId = null; // Limpiar la referencia de respuesta

            console.log("Todos los mensajes enviados con éxito.");
        } catch (error) {
            console.error("Error al enviar el mensaje:", error);
        }
    };

    async function subscribeToPocketBase() {
        // Suscripción a la colección de mensajes
        await pb.collection('mensaje').subscribe('*', async (e) => { 
            await fetchMessages(); // Refrescar los mensajes en tiempo real
            unreadCount = countUnreadMessages();

            if (e.action === 'create') {
                console.log("Mensajes no leídos actualizados:", unreadCount);
                toasts.add({
                    description: `Nuevo mensaje recibido.`,
                    duration: 3000,
                    placement: 'bottom-right',
                    type: 'info',
                    theme: 'dark',
                    showProgress: true
                });
            }
        });
    }

    // Nueva función para actualizar el estado de los mensajes
    const markMessagesAsRead = async () => {
        try {
            await Promise.all(messages.map(message => {
                if (message.sta === true && message.recipient === userId) {
                    return pb.collection("mensaje").update(message.id, { sta: false });
                }
            }));
            // Actualiza el conteo de mensajes no leídos después de marcar como leídos
            unreadCount = countUnreadMessages();
        } catch (error) {
            console.error("Error al marcar mensajes como leídos:", error);
        }
    };

    // Función para contar los mensajes con sta: true
    const countUnreadMessages = () => {
        const count = messages.filter(message => message.sta === true && message.recipient === userId).length;
        return count;
    };

    onMount(() => {
        userId = getUserFromLocalStorage().id; // Obtener el ID del usuario actual
        fetchMessages();
        fetchAllUsers(); // Cargar todos los usuarios al montar el componente
        subscribeToPocketBase();
    });

    onDestroy(() => {
        pb.collection('mensaje').unsubscribe();
    });

    function formatDate(dateString) {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    // Función para manejar la selección de destinatarios
    function handleRecipientSelection(userId) {
        if (!selectedRecipients.includes(userId)) {
            selectedRecipients = [...selectedRecipients, userId];
        } else {
            selectedRecipients = selectedRecipients.filter(id => id !== userId);
        }
    }

    // Función para manejar la respuesta a un mensaje
    function replyToMessage(message) {
        if (message.user !== userId) {
            replyToMessageId = message.id; // Establecer el ID del mensaje al que se responde
            selectedRecipients = [message.user]; // Seleccionar automáticamente los destinatarios del mensaje
            replyToMessageContent = message.content.substring(0, 20); // Obtener los primeros 20 caracteres
        } else {
            replyToMessageId = null; // Si el usuario hace clic en su propio mensaje, anula la respuesta
        }
    }

    // Función para anular la respuesta a un mensaje
    function cancelReply() {
        replyToMessageId = null; // Limpiar el ID del mensaje al que se responde
        replyToMessageContent = ""; // Limpiar el contenido al que se responde
        selectedRecipients = []; // Limpiar los destinatarios seleccionados
    }
</script>

<!-- Panel de chat -->
<div class="relative">
    <a
      class="text-white block py-1 px-3 cursor-pointer transition transform hover:scale-105 duration-200"
      href="#chat"
      on:click="{toggleChatPanel}" 
    >
        <i class="fas fa-comments"></i>
        {#if unreadCount > 0}
            <span class="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center" style="padding: 2px;">
                {unreadCount}
            </span>
        {/if}
    </a>

    <div
      bind:this="{chatPanelRef}"
      class="fixed p-4 top-0 right-0 bg-white shadow-xl text-base z-50 py-6 px-8 rounded-l-lg min-w-72 max-w-md w-full h-full overflow-y-auto transform transition-transform duration-300 ease-in-out translate-x-full hidden"
    >
        <button class="absolute top-3 right-3 text-gray-600 hover:text-red-600 transition" on:click={closeChatPanel}>
            <i class="fas fa-times"></i>
        </button>

        <h2 class="text-xl font-bold mb-6 text-center">Chat</h2>

        <h3 class="text-lg font-semibold mt-6 mb-4">Seleccionar Destinatarios:</h3>
        <div class="mb-4">
            {#each allUsers as user}
                <label class="block">
                    <input
                        type="checkbox"
                        value={user.id}
                        checked={selectedRecipients.includes(user.id)}
                        on:change={() => handleRecipientSelection(user.id)}
                    />
                    {user.name || 'Usuario desconocido'}
                </label>
            {/each}
        </div>

        <h3 class="text-lg font-semibold mt-6 mb-4">Nuevo mensaje</h3>
        {#if replyToMessageId}
            <div class="flex items-center mb-2">
                <p class="text-sm text-gray-600 mr-2">Respondiendo a: "{replyToMessageContent}"</p>
                <button 
                    on:click={cancelReply}
                    class="text-red-500 hover:underline text-xs"
                    title="Cancelar respuesta"
                >
                    <i class="fas fa-times"></i>
                </button>
            </div>
        {/if}
        
        {#if errorMessage}
            <p class="text-red-500 text-sm mb-4">{errorMessage}</p>
        {/if}
        <textarea
            bind:value="{newMessage}"
            class="w-full border border-gray-300 rounded-lg p-3 mb-4 focus:ring focus:ring-blue-300 focus:outline-none"
            placeholder="Escribe un mensaje..."
        ></textarea>
        <button on:click="{sendMessage}" class="w-full bg-blueGray-600 text-white py-2 px-4 rounded-lg shadow-lg hover:bg-blue-600 transition-all duration-300">Enviar</button>

        {#if messages.length > 0}
            <div class="h-64 overflow-y-auto border border-gray-300 rounded-lg p-4 bg-white">
                {#each messages as message (message.id)}
                    <div class="mb-4">
                        <div class="{message.user === userId ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-900'} p-3 rounded-lg shadow-md">
                            <span class="font-bold">{message.user === userId ? 'Tú' : message.userName}:</span> 
                            {message.content}
                            <span class="text-sm text-gray-500 ml-2">({formatDate(message.created)})</span>
                            {#if message.user !== userId}
                                <!-- Botón para responder solo si no es el usuario actual -->
                                <button 
                                    on:click={() => replyToMessage(message)} 
                                    class="text-blueGray-600 hover:underline ml-2 flex items-center"
                                >
                                    <i class="fas fa-reply mr-1"></i> <!-- Ícono de respuesta -->
                                    Responder
                                </button>
                            {/if}
                            {#if message.replyContent}
                                <div class="bg-gray-100 p-2 rounded-md mt-2">
                                    <p class="text-sm text-gray-600">
                                        <strong>En respuesta de:</strong> "{message.replyContent.substring(0, 20)}" <span class="text-xs text-gray-500 ml-2">({formatDate(message.replyCreated)})</span>
                                    </p>
                                </div>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        {:else}
            <p class="text-gray-500">No hay mensajes aún.</p>
        {/if}
    </div>
</div>